import styled, { css } from 'styled-components';

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;

  ${({ theme: { spacing } }) => css`
    padding: ${spacing.level6} ${spacing.level6} 0;
    gap: ${spacing.level2};
  `}

  @media (max-width: 768px) {
    ${({ theme: { spacing } }) => css`
      padding: ${spacing.level5} ${spacing.level4} 0;
    `}
  }
`;

export const Description = styled.p`
  margin: 0;

  ${({ theme: { font, colors, spacing } }) => css`
    font-size: ${font.bodyMedium.fontSize};
    color: ${colors.onSurfaceVariant};
    padding: 0 ${spacing.level4};
    line-height: 1.5;
  `}
`;

export const InputRow = styled.div`
  display: flex;
  align-items: flex-end;

  ${({ theme: { spacing } }) => css`
    gap: ${spacing.level3};
    padding: ${spacing.level2} ${spacing.level4};
  `}

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;

  ${({ theme: { spacing } }) => css`
    gap: ${spacing.level2};
  `}
`;

export const SmallLabel = styled.label`
  ${({ theme: { font, colors } }) => css`
    font-size: ${font.labelSmall.fontSize};
    font-weight: ${font.labelSmall.fontWeight};
    color: ${colors.onSurfaceVariant};
    text-transform: uppercase;
    letter-spacing: 0.05em;
  `}
`;

export const Input = styled.input<{ $hasError?: boolean }>`
  ${({ theme: { colors, font, spacing, borderRadius }, $hasError }) => css`
    padding: ${spacing.level3} ${spacing.level4};
    border-radius: ${borderRadius.small};
    border: 1px solid ${$hasError ? colors.error : colors.outline};
    background: transparent;
    color: ${colors.onSurface};
    font-size: ${font.bodyMedium.fontSize};
    font-family: ${font.bodyMedium.fontFamily};

    &:focus {
      outline: none;
      border-color: ${$hasError ? colors.error : colors.primary};
    }

    &::placeholder {
      color: ${colors.onSurfaceVariant};
      opacity: 0.45;
    }
  `}
`;

export const InputError = styled.span`
  display: block;

  ${({ theme: { font, colors, spacing } }) => css`
    font-size: ${font.bodySmall.fontSize};
    color: ${colors.error};
    padding: 0 ${spacing.level4};
    margin-top: calc(-${spacing.level2});
  `}
`;

export const WarningText = styled.p`
  margin: 0;

  ${({ theme: { font, colors, spacing } }) => css`
    font-size: ${font.bodySmall.fontSize};
    color: ${colors.error};
    font-weight: ${font.labelMedium.fontWeight};
    padding: ${spacing.level2} ${spacing.level4};
  `}
`;

export const KeyDisplay = styled.div`
  display: flex;
  align-items: center;

  ${({ theme: { spacing, colors, font, borderRadius } }) => css`
    gap: ${spacing.level3};
    padding: ${spacing.level3} ${spacing.level4};
    background: ${colors.surfaceContainerHigh};
    border-radius: ${borderRadius.small};
    margin: 0 ${spacing.level4};
    font-family: monospace;
    font-size: ${font.bodySmall.fontSize};
    color: ${colors.onSurface};
    word-break: break-all;
  `}
`;

export const RunnerTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  ${({ theme: { font, colors, spacing } }) => css`
    font-size: ${font.bodyMedium.fontSize};

    th,
    td {
      padding: ${spacing.level3} ${spacing.level4};
      text-align: left;
      border-bottom: 1px solid ${colors.outlineVariant};
      white-space: nowrap;
    }

    th {
      font-weight: ${font.labelMedium.fontWeight};
      color: ${colors.onSurfaceVariant};
      font-size: ${font.labelSmall.fontSize};
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
  `}
`;

export const StatusDot = styled.span<{ $color: string }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;

  ${({ $color }) => css`
    background: ${$color};
  `}
`;

export const MonoText = styled.span`
  font-family: monospace;

  ${({ theme: { font } }) => css`
    font-size: ${font.bodySmall.fontSize};
  `}
`;

export const DownloadSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  ${({ theme: { spacing, colors, borderRadius } }) => css`
    padding: ${spacing.level4};
    margin: 0 ${spacing.level4};
    background: ${colors.surfaceContainerHigh};
    border-radius: ${borderRadius.large};
    gap: ${spacing.level4};
  `}

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const DownloadActions = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;

  ${({ theme: { spacing } }) => css`
    gap: ${spacing.level2};
  `}
`;

export const InfoButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 6px;
  cursor: pointer;
  border-radius: 50%;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;

  ${({ theme: { colors } }) => css`
    color: ${colors.onSurface};

    &:hover {
      color: ${colors.primary};
      background-color: rgba(255, 255, 255, 0.1);
    }
  `}
`;

export const InfoDialogBody = styled.div`
  display: flex;
  flex-direction: column;

  ${({ theme: { spacing } }) => css`
    padding: ${spacing.level3} 0;
  `}
`;

export const InfoDialogContent = styled.div`
  line-height: 1.6;

  ${({ theme: { colors, font, spacing } }) => css`
    font-size: ${font.bodyMedium.fontSize};
    color: ${colors.onSurfaceVariant};

    p {
      margin: 0 0 ${spacing.level4};
    }

    p:last-child {
      margin-bottom: 0;
    }

    strong {
      font-weight: 600;
      color: ${colors.onSurface};
    }

    a {
      color: ${colors.primary};
      font-weight: ${font.labelLarge.fontWeight};
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }

    ul,
    ol {
      margin: 0 0 ${spacing.level4};
      padding-left: 1.5em;
    }

    li {
      margin-bottom: ${spacing.level2};
    }
  `}
`;

export const DownloadLabel = styled.span`
  ${({ theme: { font, colors } }) => css`
    font-size: ${font.bodyMedium.fontSize};
    color: ${colors.onSurface};
  `}
`;

export const EmptyState = styled.p`
  ${({ theme: { font, colors, spacing } }) => css`
    font-size: ${font.bodyMedium.fontSize};
    color: ${colors.onSurfaceVariant};
    padding: ${spacing.level4};
    text-align: center;
  `}
`;
